import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useDeviceType } from '@cian/ui-kit';
import { numberToPrettyString } from '@cian/utils';

import {
  RateOurEstimationRoot,
  MobileView,
  DesktopView,
  RateOurEstimationForm,
  RateOurEstimationThanks,
} from '../../components/RateOurEstimation';
import { ERateValues, ERateScreen } from '../../types/redux/rateOurEstimation';
import {
  setRateOurEstimationValue,
  setRateOurComment,
  toggleRateOurModal,
  saveScoreRateOurEstimation,
  saveSuburbanScoreRateOurEstimation,
} from '../../actions/rateOurEstimation';
import { useSetUserPrice } from './utils';
import { getRateOurEstimation } from '../../selectors/rateOurEstimation';
import { useIsReportStatusDefault } from '../../utils/report';
import { useTrackUserDisagree } from './tracking';
import { selectIsSuburban } from '../../selectors/filters';
import { TThunkDispatch } from '../../types/redux';
import { selectDealType } from '../../selectors/page';
import { selectEstimationByDealType } from '../../selectors/estimation';

export const RateOurEstimationContainer: React.FC = () => {
  const dispatch = useDispatch<TThunkDispatch>();
  const dealType = useSelector(selectDealType);
  const estimation = useSelector(selectEstimationByDealType(dealType));
  const isDesktop = useDeviceType() === 'desktop';
  const isSuburban = useSelector(selectIsSuburban);
  const { isScoreFact, isOpen, isLoading, userPrice, comment, rateOurEstimationValue, screen, isLoadingBlock } =
    useSelector(getRateOurEstimation);
  const isReportStatusDefault = useIsReportStatusDefault();
  const priceValue = userPrice ? numberToPrettyString(userPrice, ' ') : '';
  const trackUserDisagree = useTrackUserDisagree();

  const Wrapper = isDesktop ? DesktopView : MobileView;

  const onChangeUserPrice = useSetUserPrice();
  const onChangeRateOurEstimationValue = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(setRateOurEstimationValue(e.target.value as ERateValues));
    },
    [dispatch],
  );
  const onChangeComment = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      dispatch(setRateOurComment(e.target.value));
    },
    [dispatch],
  );
  const onOpenModal = React.useCallback(() => {
    trackUserDisagree();
    dispatch(toggleRateOurModal(true));
  }, [dispatch, trackUserDisagree]);
  const onCloseModal = React.useCallback(() => dispatch(toggleRateOurModal(false)), [dispatch]);
  const onSubmitForm = React.useCallback(() => {
    dispatch(isSuburban ? saveSuburbanScoreRateOurEstimation() : saveScoreRateOurEstimation());
  }, [dispatch, isSuburban]);
  const onAgreedSubmit = React.useCallback(() => {
    dispatch(setRateOurEstimationValue(ERateValues.Agreed));
    onSubmitForm();
  }, [dispatch, onSubmitForm]);

  if (isScoreFact || !isReportStatusDefault || isLoadingBlock || !estimation.data) {
    return null;
  }

  return (
    <>
      {screen === ERateScreen.Form ? (
        <RateOurEstimationRoot onOpenModal={onOpenModal} onSuccess={onAgreedSubmit} isLoading={isLoading} />
      ) : (
        <RateOurEstimationThanks isDesktop={isDesktop} />
      )}
      <Wrapper isOpen={isOpen} onClose={onCloseModal}>
        <RateOurEstimationForm
          isDesktop={isDesktop}
          isLoading={isLoading}
          priceValue={priceValue}
          comment={comment}
          onChangeUserPrice={onChangeUserPrice}
          onChangeComment={onChangeComment}
          rateOurEstimationValue={rateOurEstimationValue}
          rateOnChange={onChangeRateOurEstimationValue}
          onSubmit={onSubmitForm}
          onClose={onCloseModal}
        />
      </Wrapper>
    </>
  );
};

RateOurEstimationContainer.displayName = 'RateOurEstimationContainer';
